// Attendance for teachers and admins: recent classes with where their registers stand, and the
// register itself (each student absent, present online or present offline). Students who tapped
// "Join on Teams" start as present online, for the teacher to check.
import { useEffect, useLayoutEffect, useMemo, useState } from 'preact/hooks';
import { Link, useLeaveGuard } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Loading, Notice, Section, useRpc, useToast } from '../ui/components.jsx';
import { dateShort, dateTile, dateTime, istDate, plural, timeLabel, timeRange } from '../format.js';

export const STATUS_LABEL = { absent: 'Absent', online: 'Present online', offline: 'Present offline' };
const OPTIONS = [['absent', 'Absent'], ['online', 'Online'], ['offline', 'Offline']];

function registerText(r) {
  if (!r || !r.taken) return 'Not taken';
  return `${r.present} of ${r.marked} present`;
}

function DateTile({ iso }) {
  const t = dateTile(iso);
  return <span class="date-tile"><strong>{t.day}</strong><small>{t.month}</small></span>;
}

function SessionLine({ s }) {
  const taken = s.register && s.register.taken;
  return (
    <li>
      <Link class="list-row register-row" href={`/attendance/${s.id}`}>
        <DateTile iso={istDate(s.starts_at)} />
        <span class="list-text">
          <strong>{s.title || 'Class'}</strong>
          <small>{s.batch.name} · {timeLabel(s.starts_at)} · {plural(Number(s.on_register) || 0, 'student')}</small>
        </span>
        <Chip tone={taken ? 'ok' : 'bad'}>{registerText(s.register)}</Chip>
      </Link>
    </li>
  );
}

export function AttendanceList() {
  const { reloadHome } = useApp();
  const { data, error, loading, reload } = useRpc('staff_attendance', {});
  useEffect(() => { reloadHome(true); }, []);
  const groups = useMemo(() => {
    const all = (data && data.sessions) || [];
    const waiting = all.filter((s) => s.due);
    const rest = all.filter((s) => !waiting.includes(s));
    return { waiting, rest };
  }, [data]);
  return (
    <Shell title="Attendance">
      <p class="muted">Classes from the last 30 days. Tick each student absent, present online or present offline; students who tapped Join on Teams start as present online.</p>
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.sessions.length ? (
        <Empty icon="calendar" title="No classes yet" action={<Link class="btn btn-navy" href="/timetable"><Icon name="calendar" /><span>Timetable</span></Link>}>
          Attendance is taken for the classes in the timetable. Add your batch’s classes there first.
        </Empty>
      ) : null}
      {groups.waiting.length ? (
        <Section title={`Waiting for attendance (${groups.waiting.length})`} id="a-waiting">
          <ul class="list">{groups.waiting.map((s) => <SessionLine s={s} key={s.id} />)}</ul>
        </Section>
      ) : null}
      {groups.rest.length ? (
        <Section title="Recent classes" id="a-recent">
          <ul class="list">{groups.rest.map((s) => <SessionLine s={s} key={s.id} />)}</ul>
        </Section>
      ) : null}
    </Shell>
  );
}

export function Register({ id }) {
  const toast = useToast();
  const { reloadHome } = useApp();
  const { data, error, loading, reload, setData } = useRpc('staff_register', { p_session_id: id }, [id]);
  const [marks, setMarks] = useState({});
  const [saved, setSaved] = useState({});
  const [busy, setBusy] = useState(false);
  const [askUnmarked, setAskUnmarked] = useState(false);
  useLayoutEffect(() => { // start from what is saved; students who joined on Teams start as present online
    if (!data) return;
    const from = {};
    const start = {};
    for (const st of data.students) {
      from[st.id] = st.status || null;
      start[st.id] = st.status || (st.joined_at && !data.taken_at ? 'online' : null);
    }
    setSaved(from);
    setMarks(start);
  }, [data]);
  const changed = data ? data.students.filter((st) => (marks[st.id] || null) !== (saved[st.id] || null)) : [];
  useLeaveGuard(changed.length > 0, 'Leave without saving this attendance?');

  if (loading && !data) return <Shell backTo="/attendance" backLabel="Attendance"><Loading rows={5} /></Shell>;
  if (error) return <Shell backTo="/attendance" backLabel="Attendance"><ErrorBox error={error} onRetry={reload} /></Shell>;
  if (!data) return null;
  const s = data.session;
  const count = (k) => data.students.filter((st) => marks[st.id] === k).length;
  const unmarked = data.students.filter((st) => !marks[st.id]).length;
  const joined = data.students.filter((st) => st.joined_at).length;
  const fillRest = (status) => Object.fromEntries(data.students.map((st) => [st.id, marks[st.id] || status]));
  const setAll = (status) => setMarks(fillRest(status));
  // Everyone needs a mark for the register to count as taken; until then the class stays on the list.
  const save = async (withMarks = marks, anyway = false) => {
    if (!anyway && data.students.some((st) => !withMarks[st.id])) { setAskUnmarked(true); return; }
    setAskUnmarked(false);
    setBusy(true);
    try {
      const diff = data.students.filter((st) => (withMarks[st.id] || null) !== (saved[st.id] || null));
      const res = await rpc('staff_save_register', { p_session_id: id, p_marks: Object.fromEntries(diff.map((st) => [st.id, withMarks[st.id] || null])) });
      setData(res);
      reloadHome(true);
      const present = res.students.filter((st) => st.status === 'online' || st.status === 'offline').length;
      const absent = res.students.filter((st) => st.status === 'absent').length;
      const left = res.students.length - present - absent;
      toast(left ? `Saved. ${left === 1 ? '1 student is' : `${left} students are`} still to mark, so the class stays on your list.`
        : `Attendance saved: ${present} present, ${absent} absent`);
    } catch (ex) { toast(ex.message, 'bad'); }
    setBusy(false);
  };
  return (
    <Shell backTo="/attendance" backLabel="Attendance">
      <p class="eyebrow">{s.batch.name} · {dateShort(istDate(s.starts_at))}</p>
      <h1 class="page-title">{s.title || 'Class'}</h1>
      <p class="muted">{timeRange(s.starts_at, s.ends_at)} · {plural(data.students.length, 'student')}</p>
      {data.taken_at ? <Notice icon="done">Taken by {data.taken_by || 'a teacher'}, {dateTime(data.taken_at)}. Changes you save replace it.</Notice> : null}
      {!data.taken_at && joined ? <Notice icon="info">{joined === 1 ? '1 student' : `${joined} students`} tapped Join on Teams, so they start as present online. Check, then save.</Notice> : null}
      {s.cancelled ? <Notice tone="warn" icon="alert">This class was cancelled, so it has no attendance.</Notice> : null}
      {!s.cancelled && !data.can_take ? <Notice icon="clock">Attendance opens 30 minutes before the class starts.</Notice> : null}
      {!data.students.length ? <Empty icon="user" title="No students on this register">Students enrolled in the batch on the day of the class appear here.</Empty> : null}
      {data.can_take && data.students.length ? (
        <>
          <p class="register-sum" role="status">
            Present: <strong>{count('online')}</strong> online, <strong>{count('offline')}</strong> offline · Absent: <strong>{count('absent')}</strong>
            {unmarked ? <> · Not marked: <strong>{unmarked}</strong></> : null}
          </p>
          {unmarked ? (
            <div class="row">
              <Button variant="line" size="sm" icon="user" onClick={() => setAll('offline')}>Mark the rest present offline</Button>
              <Button variant="ghost" size="sm" onClick={() => setAll('absent')}>Mark the rest absent</Button>
            </div>
          ) : null}
          <p class="field-hint">Online and Offline both count as present.</p>
          <ul class="register">
            {data.students.map((st) => (
              <li key={st.id} class={marks[st.id] ? `is-${marks[st.id]}` : 'is-unmarked'}>
                <fieldset>
                  <legend>
                    <span class="register-name">{st.name}</span>
                    {st.joined_at ? <small>Joined on Teams {timeLabel(st.joined_at)}</small> : null}
                  </legend>
                  <div class="seg3">
                    {OPTIONS.map(([k, label]) => (
                      <label key={k} class={`seg3-opt t-${k}${marks[st.id] === k ? ' is-on' : ''}`}>
                        <input type="radio" name={`r-${st.id}`} value={k} checked={marks[st.id] === k} onChange={() => setMarks({ ...marks, [st.id]: k })} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </li>
            ))}
          </ul>
          <div class="register-save">
            {askUnmarked ? (
              <Notice tone="warn" icon="alert"><div>
                <span>{unmarked === 1 ? '1 student isn’t marked.' : `${unmarked} students aren’t marked.`} The register counts as taken once everyone is.</span>
                <span class="row">
                  <Button variant="gold" size="sm" busy={busy} onClick={() => { const m = fillRest('absent'); setMarks(m); save(m); }}>Mark them absent and save</Button>
                  <Button variant="line" size="sm" busy={busy} onClick={() => save(marks, true)}>Save, finish later</Button>
                </span>
              </div></Notice>
            ) : null}
            <Button variant="gold" icon="check" busy={busy} disabled={!changed.length} onClick={() => save()}>
              {changed.length ? `Save attendance (${plural(changed.length, 'change')})` : 'Saved'}
            </Button>
          </div>
        </>
      ) : null}
    </Shell>
  );
}
